/**
 * /connect/mcp — OAuth 2.1 consent screen for the MCP launch.
 *
 * Lifecycle:
 *  1. /api/oauth/authorize on api.trodo.ai validates the incoming OAuth
 *     request, then 302s the browser here with `?flow=<base64url-state>`.
 *  2. ProtectedRoute (App.js) preserves the URL through login. After auth,
 *     this component fetches the user's teams via /team/details, decodes
 *     the flow blob, and renders the consent form.
 *  3. On submit it POSTs to /api/oauth/consent_decision, which mints the
 *     auth code and returns `{ redirect_to: "<client redirect_uri>?code=..." }`.
 *     window.location.href continues the OAuth flow at the calling client.
 *
 * Tier gate: teams whose `subscription.plan` resolves to anything other
 * than pro/trial/enterprise are disabled with an upgrade CTA pointing at
 * /<team>/settings/billing.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ExternalLink, Pencil, ShieldAlert } from 'lucide-react';

import axiosInstance from 'shared/services/api/axiosInstance';
import {
  mcpOauthConnectPageView,
  mcpOauthTeamContext,
  mcpOauthConsentEvent,
} from 'features/connect/mcpConnectAnalytics';

// The docs site serves at the root (lib/source.ts → baseUrl: '/'), so the
// old /docs/mcp/introduction form 404s. The page is content/docs/mcp.mdx.
const DOCS_URL = 'https://docs.trodo.ai/mcp';
const PRIVACY_URL = 'https://trodo.ai/privacy-policy';
const TERMS_URL = 'https://trodo.ai/terms-of-service';

// The scope catalog is fetched from GET /mcp/keys/scopes, which serves
// backend/config/mcpScopes.js. It used to be hardcoded here and had drifted to
// 4 of the 6 scopes: mcp:issue and mcp:evals were missing, so `!catalog[s]?.is_pii`
// read `!undefined` — true — and defaulted them ON, while the render below
// dropped anything it couldn't describe. Both are in DEFAULT_SCOPES, so the
// ordinary OAuth path granted a connector the right to delete an evaluator or
// reopen an issue on a screen that never mentioned either. Unknown scopes now
// fail closed and are shown, not hidden.

function decodeFlow(flow) {
  if (!flow || typeof flow !== 'string') return null;
  try {
    const b64 = flow.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const txt = atob(padded);
    const obj = JSON.parse(txt);
    if (obj && obj.v === 1) return obj;
  } catch (_) {
    /* fall through */
  }
  return null;
}

function planOfTeam(team) {
  return String(team?.subscription?.plan || 'free').toLowerCase();
}

function isPaidTeam(team) {
  const plan = planOfTeam(team);
  // A trial is paid access for gating purposes — locking a trialling team out
  // of MCP is the one thing that makes the trial not a trial.
  return plan === 'pro' || plan === 'trial' || plan === 'enterprise';
}

export default function ConnectMCP() {
  const [searchParams] = useSearchParams();
  const flowToken = searchParams.get('flow') || '';
  const flowInfo = useMemo(() => decodeFlow(flowToken), [flowToken]);

  const [teams, setTeams] = useState(null); // null = loading
  const [teamsErr, setTeamsErr] = useState('');
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [catalog, setCatalog] = useState(null);   // null = loading, [] = failed
  const [catalogErr, setCatalogErr] = useState('');
  const [scopes, setScopes] = useState({});       // set once the catalog lands
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Defaults wait for the catalog: is_pii can't be read off a scope we haven't
  // been told about, and guessing is what caused the silent grant.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axiosInstance.get('/mcp/keys/scopes');
        if (!alive) return;
        const list = Array.isArray(data?.scopes) ? data.scopes : [];
        setCatalog(list);
        const byId = Object.fromEntries(list.map((s) => [s.id, s]));
        const requested = (flowInfo && flowInfo.requested_scopes) || [];
        setScopes(Object.fromEntries(
          // Everything the client asked for, including PII. There is no longer
          // a per-scope choice here: the picker asked a question most people
          // could not answer, and a wrong answer produced a connection that
          // silently could not do what they wanted. Unknown scopes stay off —
          // we will not grant a permission we cannot describe.
          requested.map((s) => [s, !!byId[s]]),
        ));
      } catch (e) {
        if (!alive) return;
        setCatalog([]);
        setCatalogErr(
          e?.response?.data?.error || e?.message || 'Failed to load permissions',
        );
      }
    })();
    return () => { alive = false; };
  }, [flowInfo]);

  const loadTeams = useCallback(async () => {
    setTeamsErr('');
    try {
      const { data } = await axiosInstance.get('/team/details');
      const list = Array.isArray(data?.team) ? data.team : [];
      setTeams(list);
      const firstPaid = list.find(isPaidTeam);
      const fallback = list[0];
      if ((firstPaid || fallback) && selectedTeamId == null) {
        setSelectedTeamId((firstPaid || fallback).id);
      }
    } catch (e) {
      setTeams([]);
      setTeamsErr(e?.response?.data?.message || e?.message || 'Failed to load your teams');
    }
  }, [selectedTeamId]);

  useEffect(() => { loadTeams(); }, [loadTeams]);

  useEffect(() => {
    if (!flowInfo) return undefined;
    let alive = true;
    (async () => {
      try {
        if (!alive) return;
        await mcpOauthConnectPageView(flowInfo);
      } catch (e) {
        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.warn('MCP connect page view analytics', e);
        }
      }
    })();
    return () => { alive = false; };
  }, [flowInfo]);

  const mcpTeamSelectInitialRef = useRef(true);
  const mcpLastTrackedTeamIdRef = useRef(null);
  useEffect(() => {
    mcpTeamSelectInitialRef.current = true;
    mcpLastTrackedTeamIdRef.current = null;
  }, [flowToken]);

  useEffect(() => {
    if (!flowInfo || !teams || selectedTeamId == null) return undefined;
    if (String(mcpLastTrackedTeamIdRef.current) === String(selectedTeamId)) return undefined;
    const team = teams.find((t) => String(t.id) === String(selectedTeamId));
    if (!team) return undefined;
    const source = mcpTeamSelectInitialRef.current ? 'initial' : 'user_change';
    mcpTeamSelectInitialRef.current = false;
    mcpLastTrackedTeamIdRef.current = selectedTeamId;
    let alive = true;
    (async () => {
      try {
        if (!alive) return;
        await mcpOauthTeamContext(team, { source });
      } catch (e) {
        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.warn('MCP team analytics', e);
        }
      }
    })();
    return () => { alive = false; };
  }, [flowInfo, selectedTeamId, teams]);

  // ── Invalid / expired flow ──────────────────────────────────────────────────
  if (!flowInfo) {
    return (
      <Shell>
        <h1 className="font-['Inter'] text-2xl font-semibold text-[color:var(--text-primary)]">
          Connect to Trodo
        </h1>
        <p className="mt-2 rounded-lg border border-[color:var(--feedback-error)] bg-[color:var(--feedback-error-bg)] px-4 py-3 text-[13px] text-[color:var(--feedback-error)]">
          This consent link is invalid or expired. Restart the connect flow from your Claude or Cursor client.
        </p>
      </Shell>
    );
  }

  const requestedScopes = flowInfo.requested_scopes || [];
  const catalogById = Object.fromEntries((catalog || []).map((s) => [s.id, s]));
  const selectedTeam = (teams || []).find((t) => String(t.id) === String(selectedTeamId)) || null;
  const selectedTeamPaid = selectedTeam ? isPaidTeam(selectedTeam) : false;
  // Known-scopes-only, belt and braces: `scopes` can't hold an unknown key
  // today, but this is the value that gets granted — it shouldn't depend on
  // that staying true.
  const enabledScopes = requestedScopes.filter((s) => scopes[s] && catalogById[s]);
  // Drives the two badges on the summary card. Derived from the same catalog
  // the server sends, so neither can claim something the grant doesn't include.
  const grantsWrite = enabledScopes.some((s) => catalogById[s]?.grants_write);
  const grantsPii = enabledScopes.some((s) => catalogById[s]?.is_pii);
  // A scope the server asked for but can't describe. Shown, never granted.
  // Suppressed when the catalog fetch itself failed: everything is unknown in
  // that case, and five "can't describe this" cards under an error banner is
  // noise. The banner is the whole story there.
  const unknownScopes = (catalog && !catalogErr)
    ? requestedScopes.filter((s) => !catalogById[s])
    : [];

  async function decide(decision) {
    setError('');
    setSubmitting(true);
    try {
      try {
        await mcpOauthConsentEvent({
          flowInfo,
          decision,
          team: selectedTeam,
          teamId: selectedTeamId,
          grantedScopes: enabledScopes,
          paid: selectedTeamPaid,
        });
      } catch (e) {
        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.warn('MCP consent analytics', e);
        }
      }
      const { data } = await axiosInstance.post('/oauth/consent_decision', {
        flow: flowToken,
        decision,
        team_id: decision === 'allow' ? selectedTeamId : null,
        scopes: decision === 'allow' ? enabledScopes : [],
      });
      if (data?.redirect_to) {
        window.location.href = data.redirect_to;
        return;
      }
      throw new Error('No redirect_to in response');
    } catch (e) {
      const msg =
        e?.response?.data?.error_description ||
        e?.response?.data?.error ||
        e?.message ||
        'Failed to complete consent';
      setError(String(msg));
      setSubmitting(false);
    }
  }

  const clientLabel = flowInfo.client_name || flowInfo.client_id || 'an MCP client';

  return (
    <Shell>
      {/* Header */}
      <div className="mb-1 flex items-center gap-2">
        <span
          className="text-[10px] font-semibold uppercase tracking-[0.18em]"
          style={{ color: 'var(--trodo-primary)' }}
        >
          Trodo · MCP
        </span>
      </div>
      <h1 className="mt-1 font-['Inter'] text-2xl font-semibold leading-tight text-[color:var(--text-primary)]">
        Allow {clientLabel} to access Trodo on your behalf
      </h1>
      <p className="mt-2 text-[14px] leading-relaxed text-[color:var(--text-muted)]">
        {/* Not "wants to read": most scopes here also grant writes, and the
            badges below say which. The sentence shouldn't undercut them. */}
        A connector wants to access your team's data through Trodo's MCP server. You're in control: pick the team and permissions below.
      </p>

      {/* Team selector */}
      <SectionLabel>Team</SectionLabel>
      {teams === null && <SkeletonTeamSelect />}
      {teams !== null && teams.length === 0 && (
        <div className="rounded-lg border border-dashed border-[color:var(--border-default)] bg-[color:var(--surface-sunken)] px-4 py-5 text-center text-[13px] text-[color:var(--text-muted)]">
          {teamsErr || "You don't belong to any team yet. Create one before connecting MCP."}
        </div>
      )}
      {teams !== null && teams.length > 0 && (
        <div className="space-y-2">
          <select
            className="w-full cursor-pointer rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-sunken)] py-2.5 pl-3 pr-3 font-['Inter'] text-[14px] text-[color:var(--text-primary)] shadow-sm transition-colors focus:border-[color:var(--trodo-primary)] focus:outline-none focus:ring-0"
            style={{ accentColor: 'var(--trodo-primary)' }}
            value={selectedTeamId != null ? String(selectedTeamId) : ''}
            onChange={(e) => {
              const v = e.target.value;
              if (!v) return;
              const t = teams.find((x) => String(x.id) === v);
              if (t && isPaidTeam(t)) setSelectedTeamId(t.id);
            }}
            aria-label="Select team for MCP access"
          >
            <option value="" disabled>Choose a team…</option>
            {teams.map((t) => {
              const tPaid = isPaidTeam(t);
              const label = `${t.name || `Team #${t.id}`} · ${planOfTeam(t)}${
                !tPaid ? ' — upgrade to use MCP' : ''
              }`;
              return (
                <option key={t.id} value={t.id} disabled={!tPaid}>
                  {label}
                </option>
              );
            })}
          </select>
          {selectedTeam && !selectedTeamPaid && (
            <p className="text-[12px] leading-relaxed text-[color:var(--text-muted)]">
              MCP requires a paid plan.{' '}
              <a
                href={`/${encodeURIComponent(selectedTeam.name || '')}/settings/billing`}
                className="font-medium transition-opacity hover:opacity-80"
                style={{ color: 'var(--trodo-primary)' }}
              >
                Upgrade to enable MCP →
              </a>
            </p>
          )}
        </div>
      )}

      {/* Permissions */}
      <SectionLabel>Permissions</SectionLabel>
      <div className="flex flex-col gap-2">
        {catalog === null && <SkeletonScopes />}
        {catalog !== null && requestedScopes.length === 0 && (
          <p className="text-[13px] text-[color:var(--text-muted)]">No scopes requested.</p>
        )}
        {catalogErr && (
          <div className="rounded-lg border border-[color:var(--feedback-error)] bg-[color:var(--feedback-error-bg)] px-4 py-3 text-[13px] text-[color:var(--feedback-error)]">
            Couldn't load the permission list, so there's nothing to consent to
            safely. Reload the page, or restart the connect flow from your
            client. ({catalogErr})
          </div>
        )}
        {unknownScopes.map((s) => (
          <div
            key={s}
            className="rounded-lg border border-dashed px-3 py-3 text-[12px] leading-relaxed text-[color:var(--text-muted)]"
            style={{ borderColor: 'var(--feedback-warning)' }}
          >
            <span className="font-['Inter'] text-[13px] font-semibold text-[color:var(--text-primary)]">
              {s}
            </span>{' '}
            — this client asked for a permission this app can't describe, so it
            won't be granted. Update Trodo, or continue without it.
          </div>
        ))}
        {catalog !== null && requestedScopes.some((s) => catalogById[s]) && (
          /* One summary, not a checkbox per scope.
             The per-scope picker is gone: see the comment on setScopes above —
             but a consent screen that says nothing about what it grants is not
             consent, so this still states the shape of the access and calls out
             the two things a reasonable person would want to know: that it can
             write, and that it can read user identities. Fine-grained control
             lives in the MCP client, which lists every tool individually. */
          <div className="rounded-lg border border-[color:var(--border-default)] bg-[color:var(--surface-base)] p-4">
            <p className="text-[13px] leading-relaxed text-[color:var(--text-primary)]">
              This connection can read your team's Trodo data: agent runs and
              traces, issues, evaluations, prompts, datasets and product
              analytics.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {grantsWrite && (
                <span className="inline-flex items-center gap-1 rounded-full bg-[color:var(--feedback-warning-bg)] px-2 py-0.5 text-[10px] font-semibold text-[color:var(--feedback-warning)]">
                  <Pencil size={10} aria-hidden /> can make changes
                </span>
              )}
              {grantsPii && (
                <span className="inline-flex items-center gap-1 rounded-full bg-[color:var(--feedback-error-bg)] px-2 py-0.5 text-[10px] font-semibold text-[color:var(--feedback-error)]">
                  <ShieldAlert size={10} aria-hidden /> includes user identities
                </span>
              )}
            </div>
            <p className="mt-3 text-[12px] leading-relaxed text-[color:var(--text-muted)]">
              Your MCP client lists every tool separately: disable any you don't
              want, and it will ask before running anything that makes changes.
            </p>
          </div>
        )}
      </div>

      {/* Error banner */}
      {error && (
        <div className="mt-4 rounded-lg border border-[color:var(--feedback-error)] bg-[color:var(--feedback-error-bg)] px-4 py-3 text-[13px] text-[color:var(--feedback-error)]">
          {error}
        </div>
      )}

      {/* Action buttons */}
      <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end">
        <button
          type="button"
          onClick={() => decide('deny')}
          disabled={submitting}
          className="rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-base)] px-4 py-2 text-[13px] font-medium text-[color:var(--text-primary)] transition-colors hover:bg-[color:var(--surface-hover)] disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => {
            if (submitting) return;
            if (!selectedTeamPaid) {
              setError('MCP requires a paid plan. Upgrade your team to enable MCP access.');
              return;
            }
            if (enabledScopes.length === 0) {
              setError('Enable at least one permission scope to continue.');
              return;
            }
            decide('allow');
          }}
          disabled={submitting}
          aria-disabled={submitting || !selectedTeamPaid || enabledScopes.length === 0}
          className={`rounded-md px-4 py-2 text-[13px] font-medium text-ink-onBrand transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50${
            (!submitting && (!selectedTeamPaid || enabledScopes.length === 0))
              ? ' cursor-not-allowed opacity-50'
              : ''
          }`}
          style={{ backgroundColor: 'var(--trodo-primary)' }}
        >
          {submitting ? 'Connecting…' : 'Allow access'}
        </button>
      </div>

      {/* Footer */}
      <div className="mt-6 border-t border-[color:var(--border-default)] pt-4 text-center text-[11px] leading-relaxed text-[color:var(--text-muted)]">
        Revoke access any time from{' '}
        <strong className="text-[color:var(--text-primary)]">Settings → MCP</strong>.
        <div className="mt-1 flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
          <a
            href={DOCS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 transition-opacity hover:opacity-80"
            style={{ color: 'var(--trodo-primary)' }}
          >
            Docs <ExternalLink size={10} aria-hidden />
          </a>
          <span className="text-[color:var(--text-disabled)]">·</span>
          <a
            href={PRIVACY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="transition-opacity hover:opacity-80"
            style={{ color: 'var(--trodo-primary)' }}
          >
            Privacy
          </a>
          <span className="text-[color:var(--text-disabled)]">·</span>
          <a
            href={TERMS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="transition-opacity hover:opacity-80"
            style={{ color: 'var(--trodo-primary)' }}
          >
            Terms
          </a>
        </div>
      </div>
    </Shell>
  );
}

// ---------------------------------------------------------------------------
// Layout helpers
// ---------------------------------------------------------------------------

function Shell({ children }) {
  // App uses html,body { overflow:hidden } + #root { height:100% } — this page
  // must scroll inside the viewport, not the document.
  return (
    <div
      className="max-h-[100dvh] min-h-0 w-full overflow-y-auto overflow-x-hidden bg-[color:var(--surface-base)] py-6 scrollbar-transparent sm:py-10"
      style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
    >
      <div className="mx-auto w-full max-w-[560px] px-4 pb-10">
        <div className="overflow-visible rounded-lg border border-[color:var(--border-default)] bg-[color:var(--surface-sunken)] p-6 sm:p-8">
          {children}
        </div>
      </div>
    </div>
  );
}

function SectionLabel({ children }) {
  return (
    <div className="mb-2 mt-6 text-[10px] font-semibold uppercase tracking-[0.18em] text-[color:var(--text-muted)]">
      {children}
    </div>
  );
}

function SkeletonTeamSelect() {
  return (
    <div
      className="h-10 w-full max-w-md animate-pulse rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-hover)]"
      aria-hidden
    />
  );
}

function SkeletonScopes() {
  return (
    <>
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="h-[72px] w-full animate-pulse rounded-lg border border-[color:var(--border-default)] bg-[color:var(--surface-hover)]"
          aria-hidden
        />
      ))}
    </>
  );
}
